import { FoliageDaySummary, FoliagePoiProfile, Manifest } from '../types';
import * as pmtiles from 'pmtiles';
import { getTileUrl, getOrCreatePMTiles } from '../config';

export class FoliageService {
  private static cache: Map<string, { days: FoliageDaySummary[]; profile: FoliagePoiProfile }> = new Map();

  /**
   * Computes default foliage calendar selection date & month index based on real-time date bounds:
   * - If actual date < Sept 1, default to Sept 1.
   * - If actual date > Nov 30, default to Nov 30.
   * - Else default to actual date.
   */
  public static getDefaultDateInfo(nowDate: Date = new Date()): { dateStr: string; monthIndex: number } {
    const year = nowDate.getFullYear();
    const sept1 = new Date(year, 8, 1);
    const nov30 = new Date(year, 10, 30);

    let targetDate = nowDate;
    if (nowDate < sept1) {
      targetDate = sept1;
    } else if (nowDate > nov30) {
      targetDate = nov30;
    }

    const yyyy = targetDate.getFullYear();
    const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
    const dd = String(targetDate.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;

    const monthNum = targetDate.getMonth() + 1; // 9 = Sept, 10 = Oct, 11 = Nov
    const monthIndex = Math.max(0, Math.min(2, monthNum - 9));

    return { dateStr, monthIndex };
  }

  /**
   * Astronomical photoperiod daylength in hours for a given latitude and Day of Year (1..365).
   */
  public static getPhotoperiod(latDeg: number, doy: number): number {
    const phi = (latDeg * Math.PI) / 180.0;
    const delta = (23.44 * Math.PI) / 180.0 * Math.sin(((2.0 * Math.PI) / 365.0) * (doy - 80));
    const tanProd = Math.max(-1.0, Math.min(1.0, -Math.tan(phi) * Math.tan(delta)));
    const omega = Math.acos(tanProd);
    return (24.0 / Math.PI) * omega;
  }

  /**
   * Climatological / forecast daily minimum temperature (°C) for a given point and DOY.
   */
  public static getEstimatedTmin(lat: number, lon: number, elevMeters: number, doy: number): number {
    const fallProg = Math.max(0.0, Math.min(1.0, (doy - 244) / 90.0));
    
    // True Pacific and Atlantic coast geometry estimation
    const pacificCoastLon = lat >= 32.0 ? -117.0 - (lat - 32.0) * 0.35 : -117.0;
    const distFromPacific = Math.max(0.0, lon - pacificCoastLon);
    const atlanticCoastLon = -75.0 + (lat - 35.0) * 0.25;
    const distFromAtlantic = Math.max(0.0, atlanticCoastLon - lon);
    const distFromCoast = Math.min(distFromPacific, distFromAtlantic);

    // Pacific Maritime Zone (West of Cascade/Sierra crest, lowlands elev < 800m)
    const isWestCoast = lat >= 32.0 && lat <= 58.0 && lon <= -120.0;
    const lonMarineWeight = Math.max(0.0, Math.min(1.0, (2.6 - distFromPacific) / 2.6));
    const elevMarineWeight = Math.max(0.0, Math.min(1.0, 1.0 - (elevMeters / 800.0)));
    const marineFactor = isWestCoast ? lonMarineWeight * elevMarineWeight : 0.0;

    const continentalPenalty = Math.max(0.0, Math.min(3.5, (distFromCoast - 1.5) * 0.35)) * (1.0 - marineFactor);

    // Sea level baseline
    const contSept1 = 24.0 - ((lat - 24.0) / 41.0) * 18.0;
    const contNov30 = 14.0 - ((lat - 24.0) / 41.0) * 28.0;
    const marineSept1 = 18.0 - ((lat - 24.0) / 41.0) * 10.0;
    const marineNov30 = 10.0 - ((lat - 24.0) / 41.0) * 12.0;

    const baseSept1 = contSept1 * (1.0 - marineFactor) + marineSept1 * marineFactor;
    const baseNov30 = contNov30 * (1.0 - marineFactor) + marineNov30 * marineFactor;

    const dailySeaLevel = baseSept1 + fallProg * (baseNov30 - baseSept1);
    const elevCooling = (Math.max(0, elevMeters) / 1000.0) * 6.5;
    const radiationalCooling = continentalPenalty * (0.5 + 1.0 * fallProg);

    return dailySeaLevel - elevCooling - radiationalCooling;
  }

  /**
   * Evaluates the complete 91-day Autumn Seasonal Phenology curve for a location.
   */
  public static getSeasonalProgression(
    lat: number,
    lon: number,
    elevMeters: number = 250,
    year: number = 2026
  ): { days: FoliageDaySummary[]; profile: FoliagePoiProfile } {
    const cacheKey = `${lat.toFixed(3)}_${lon.toFixed(3)}_${Math.round(elevMeters)}_${year}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    const tBase = 15.5;
    const pCritContinental = 12.0;
    const pCritMaritime = 11.4;
    const cddContinental = 320.0;
    const cddMaritime = 420.0;

    // Pacific Maritime Continentality Index (West of Cascade/Sierra Crest, lowlands elev < 800m)
    const pacificCoastLon = lat >= 32.0 ? -117.0 - (lat - 32.0) * 0.35 : -117.0;
    const distFromPacific = Math.max(0.0, lon - pacificCoastLon);
    const isWestCoast = lat >= 32.0 && lat <= 58.0 && lon <= -120.0;
    const lonMarineWeight = Math.max(0.0, Math.min(1.0, (2.6 - distFromPacific) / 2.6));
    const elevMarineWeight = Math.max(0.0, Math.min(1.0, 1.0 - (elevMeters / 800.0)));
    const marineFactor = isWestCoast ? lonMarineWeight * elevMarineWeight : 0.0;

    // Base Biome CDD Target (interpolating between continental and maritime)
    const cddBiomeBase = cddContinental * (1.0 - marineFactor) + cddMaritime * marineFactor;

    // Continental Latitude factor (for Eastern / Midwestern continental interior)
    const latFactorContinental = 1.0 + 0.15 * ((38.0 - lat) / 12.0);
    const latFactor = (1.0 - marineFactor) * latFactorContinental + marineFactor * 1.0;

    // High elevation species scaling
    const elevFactor = Math.max(0.78, Math.min(1.0, 1.0 - 0.22 * (Math.min(3000, elevMeters) / 2400.0)));
    const cddPeakTarget = cddBiomeBase * latFactor * elevFactor;

    // Summer heat and drought stress factor modeling
    const southwestDrought = Math.exp(-Math.pow((lon + 104.0) / 7.0, 2) - Math.pow((lat - 33.0) / 4.5, 2)) * -1.85;
    const greatBasinDrought = Math.exp(-Math.pow((lon + 115.0) / 6.0, 2) - Math.pow((lat - 39.0) / 4.0, 2)) * -1.20;
    const midAtlanticDry = Math.exp(-Math.pow((lon + 78.0) / 4.0, 2) - Math.pow((lat - 38.5) / 3.0, 2)) * -0.75;
    const pnwMoisture = Math.exp(-Math.pow((lon + 122.5) / 4.0, 2) - Math.pow((lat - 45.8) / 3.2, 2)) * 0.45;
    const upperMidwestWet = Math.exp(-Math.pow((lon + 93.0) / 6.0, 2) - Math.pow((lat - 45.0) / 4.0, 2)) * 0.85;
    const sma = southwestDrought + greatBasinDrought + midAtlanticDry + pnwMoisture + upperMidwestWet;

    const swHeat = Math.exp(-Math.pow((lon + 111.0) / 8.5, 2) - Math.pow((lat - 34.5) / 5.5, 2)) * 2.80;
    const texasHeat = Math.exp(-Math.pow((lon + 99.0) / 6.0, 2) - Math.pow((lat - 31.0) / 4.0, 2)) * 2.20;
    const plainsHeat = Math.exp(-Math.pow((lon + 98.0) / 8.0, 2) - Math.pow((lat - 41.0) / 5.0, 2)) * 1.35;
    const pnwHeat = Math.exp(-Math.pow((lon + 121.0) / 5.0, 2) - Math.pow((lat - 46.0) / 3.5, 2)) * 0.65;
    const neHeat = Math.exp(-Math.pow((lon + 74.0) / 6.0, 2) - Math.pow((lat - 42.0) / 4.0, 2)) * 0.90;
    const sha = swHeat + texasHeat + plainsHeat + pnwHeat + neHeat;

    const smaStressNorm = Math.max(0.0, Math.min(1.5, -sma / 2.0));
    const shaStressNorm = Math.max(0.0, Math.min(1.5, sha / 3.0));
    const surplusBonus = Math.max(0.0, Math.min(0.10, sma * 0.04));
    const mStress = Math.max(0.78, Math.min(1.15, 1.0 - (0.08 * smaStressNorm + 0.08 * shaStressNorm) + surplusBonus));
    const decayMultiplier = Math.max(1.0, Math.min(1.70, 1.0 + 0.35 * (smaStressNorm + shaStressNorm)));
    const effectivePeakTarget = cddPeakTarget * mStress;

    const effectivePCrit = pCritContinental * (1.0 - marineFactor) + pCritMaritime * marineFactor;

    const days: FoliageDaySummary[] = [];
    let runningCdd = 0;
    let runningFrost = 0;
    let peakStart: string | undefined = undefined;
    let peakEnd: string | undefined = undefined;
    let maxFpi = 0;
    let maxStageRank = 1;

    const todayStr = new Date().toISOString().split('T')[0];

    for (let i = 0; i < 91; i++) {
      const cur = new Date(year, 8, 1 + i);
      const yyyy = cur.getFullYear();
      const mm = String(cur.getMonth() + 1).padStart(2, '0');
      const dd = String(cur.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      const doy = 244 + i;

      const photoHours = this.getPhotoperiod(lat, doy);
      const isGated = photoHours <= effectivePCrit || doy >= (265 + Math.round(marineFactor * 4));

      const tmin = this.getEstimatedTmin(lat, lon, elevMeters, doy);
      const rawChilling = Math.max(0, tBase - Math.max(-2.5, tmin));
      let chilling = Math.min(11.5, rawChilling);

      if (!isGated && doy >= 258) {
        chilling *= 0.25;
      } else if (!isGated) {
        chilling = 0;
      }
      runningCdd += chilling;

      const baseFpi = (runningCdd / effectivePeakTarget) * 100;
      const adjustedFpi = baseFpi > 105.0 ? 105.0 + (baseFpi - 105.0) * decayMultiplier : baseFpi;
      const rawFpi = Math.min(150, Math.round(adjustedFpi));

      // Monotonic FPI constraint
      maxFpi = Math.max(maxFpi, rawFpi);
      const fpi = maxFpi;

      if (tmin <= -3.0 && fpi >= 95) {
        runningFrost++;
      }

      // Stage and Color derivation
      let stageName = 'Minimal / Patchy';
      let badgeLabel = 'EARLY';
      let colorHex = '#2e7d32'; // Forest Green
      let stageRank = 1;

      if (fpi < 25) {
        stageName = 'Minimal / Patchy';
        badgeLabel = 'EARLY';
        colorHex = fpi > 12 ? '#689f38' : '#2e7d32';
        stageRank = 1;
      } else if (fpi < 55) {
        stageName = 'Moderate Color';
        badgeLabel = 'MOD';
        colorHex = fpi > 40 ? '#fbc02d' : '#8bc34a';
        stageRank = 2;
      } else if (fpi < 80) {
        stageName = 'Near Peak';
        badgeLabel = 'NEAR';
        colorHex = fpi > 68 ? '#f57c00' : '#ffa000';
        stageRank = 3;
      } else if (fpi <= 105) {
        stageName = 'Peak Foliage';
        badgeLabel = 'PEAK';
        colorHex = fpi > 92 ? '#c62828' : '#d32f2f'; // Brilliant Crimson Red
        stageRank = 4;
      } else if (fpi < 135 && runningFrost < 4) {
        stageName = 'Past Peak';
        badgeLabel = 'PAST';
        colorHex = fpi > 120 ? '#5d4037' : '#795548'; // Rustic Saddle Brown
        stageRank = 5;
      } else {
        stageName = 'Forest Senesced / Bare';
        badgeLabel = 'BARE';
        colorHex = '#78909c'; // Slate Grey
        stageRank = 6;
      }

      // Strict forward monotonic stage progression guard
      if (stageRank < maxStageRank) {
        stageRank = maxStageRank;
        if (stageRank === 2) {
          stageName = 'Moderate Color';
          badgeLabel = 'MOD';
          colorHex = '#fbc02d';
        } else if (stageRank === 3) {
          stageName = 'Near Peak';
          badgeLabel = 'NEAR';
          colorHex = '#f57c00';
        } else if (stageRank === 4) {
          stageName = 'Peak Foliage';
          badgeLabel = 'PEAK';
          colorHex = '#d32f2f';
        } else if (stageRank === 5) {
          stageName = 'Past Peak';
          badgeLabel = 'PAST';
          colorHex = '#795548';
        } else if (stageRank >= 6) {
          stageName = 'Forest Senesced / Bare';
          badgeLabel = 'BARE';
          colorHex = '#78909c';
        }
      } else {
        maxStageRank = stageRank;
      }

      // Track Peak Window (80% to 105% FPI)
      if (fpi >= 80 && !peakStart) {
        const monthShort = cur.toLocaleDateString('en-US', { month: 'short' });
        peakStart = `${monthShort} ${cur.getDate()}`;
      }
      if (fpi > 105 && !peakEnd) {
        const monthShort = cur.toLocaleDateString('en-US', { month: 'short' });
        peakEnd = `${monthShort} ${cur.getDate()}`;
      }

      days.push({
        dateStr,
        dayNum: cur.getDate(),
        monthNum: cur.getMonth() + 1,
        fpi,
        stageName,
        badgeLabel,
        colorHex,
        tminC: Math.round(tmin * 10) / 10,
        cddAccum: Math.round(runningCdd),
        isForecast: dateStr >= todayStr,
        isToday: dateStr === todayStr
      });
    }

    if (!peakEnd && peakStart) {
      peakEnd = 'Late Nov';
    }

    const peakDateRangeLabel = (peakStart && peakEnd) 
      ? `Peak: ${peakStart} – ${peakEnd}`
      : 'Peak: Mid to Late October';

    const currentDaySummary = days.find(d => d.dateStr === todayStr) || days[Math.min(34, days.length - 1)];

    let droughtStatus = 'Normal Summer Moisture';
    if (sma < -1.0) droughtStatus = 'Moderate Summer Drought (-1.2 SMA)';
    if (sma > 1.0) droughtStatus = 'Abundant Summer Rain (+0.8 SMA)';

    // Estimate Deciduous Canopy % from coordinates
    const eastern = Math.exp(-Math.pow((lon + 80.0) / 10.0, 2) - Math.pow((lat - 40.0) / 6.0, 2)) * 0.95;
    const aspen = Math.exp(-Math.pow((lon + 106.0) / 3.0, 2) - Math.pow((lat - 39.0) / 2.5, 2)) * 0.70;
    const canopyPct = Math.round(Math.min(98, Math.max(15, (eastern + aspen) * 100)));

    // Calculate Seasonal Vibrancy Category & Departure
    const peakDaysCount = days.filter(d => d.fpi >= 80 && d.fpi <= 105).length;
    const crispNights = days.filter(d => d.fpi >= 35 && d.fpi <= 105 && d.tminC !== undefined && d.tminC >= 1.0 && d.tminC <= 7.0).length;
    const warmNights = days.filter(d => d.fpi >= 35 && d.fpi <= 105 && d.tminC !== undefined && d.tminC > 11.5).length;
    
    const thermalScore = Math.max(-1.5, Math.min(1.5, ((crispNights - warmNights * 1.5) / Math.max(1, peakDaysCount + 7)) * 2.0));
    const retentionScore = Math.max(-1.0, Math.min(1.0, (peakDaysCount - 8) / 6.0));
    const moistureScore = Math.max(-1.5, Math.min(1.0, 0.3 - (Math.max(0, -sma - 0.5) * 0.6 + Math.max(0, sha / 2.5) * 0.3)));
    const vibrancyDeparture = Math.max(-25.0, Math.min(25.0, (0.35 * thermalScore + 0.20 * retentionScore + 0.15 * moistureScore) * 22.0));
    const internalVibrancyScore = Math.round(Math.max(0, Math.min(100, 70 + vibrancyDeparture)));

    let vibrancyCategory: 'Below Normal / Reduced' | 'Average / Typical' | 'Above Normal / Enhanced' = 'Average / Typical';
    if (vibrancyDeparture <= -6.0) {
      vibrancyCategory = 'Below Normal / Reduced';
    } else if (vibrancyDeparture >= 6.0) {
      vibrancyCategory = 'Above Normal / Enhanced';
    }

    const profile: FoliagePoiProfile = {
      lat,
      lon,
      elevationMeters: Math.round(elevMeters),
      currentFpi: currentDaySummary.fpi,
      currentStage: currentDaySummary.stageName,
      peakStartDate: peakStart,
      peakEndDate: peakEnd,
      peakDateRangeLabel,
      droughtStatus,
      canopyFractionPct: canopyPct,
      cddTotal: currentDaySummary.cddAccum || 0,
      vibrancyCategory,
      vibrancyScore: internalVibrancyScore
    };

    const result = { days, profile };
    this.cache.set(cacheKey, result);
    return result;
  }

  /**
   * Returns calendar grid cells for a specific month (monthIndex: 0=Sept, 1=Oct, 2=Nov).
   */
  public static getMonthCalendar(
    monthIndex: number, // 0 = Sept, 1 = Oct, 2 = Nov
    lat: number,
    lon: number,
    elevMeters: number = 250,
    year: number = 2026
  ): { days: FoliageDaySummary[]; profile: FoliagePoiProfile; firstDayCol: number; monthName: string } {
    const { days, profile } = this.getSeasonalProgression(lat, lon, elevMeters, year);
    
    const targetMonthNum = monthIndex + 9; // 9 = Sept, 10 = Oct, 11 = Nov
    const monthDays = days.filter(d => d.monthNum === targetMonthNum);

    const firstDate = new Date(year, targetMonthNum - 1, 1);
    const firstDayCol = firstDate.getDay(); // 0 = Sun, 1 = Mon, ...
    const monthName = firstDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return {
      days: monthDays,
      profile,
      firstDayCol,
      monthName
    };
  }

  private static pmtilesInstances: Map<string, pmtiles.PMTiles> = new Map();

  private static getPmtilesInstance(url: string): pmtiles.PMTiles {
    return getOrCreatePMTiles(url, this.pmtilesInstances);
  }

  /**
   * Decodes stage, badge, and color from a map tile pixel [R, G, B, A].
   */
  public static decodeFoliagePixel(r: number, g: number, b: number, a: number): {
    stageName: string;
    badgeLabel: string;
    colorHex: string;
    rank: number;
  } | null {
    if (a < 30) return null; // Transparent / ocean / outside foliage mask

    const palette = [
      { name: 'Minimal / Patchy', badge: 'EARLY', hex: '#2e7d32', rgb: [46, 125, 50], rank: 1 },
      { name: 'Moderate Color', badge: 'MOD', hex: '#fbc02d', rgb: [251, 192, 45], rank: 2 },
      { name: 'Near Peak', badge: 'NEAR', hex: '#f57c00', rgb: [245, 124, 0], rank: 3 },
      { name: 'Peak Foliage', badge: 'PEAK', hex: '#d32f2f', rgb: [211, 47, 47], rank: 4 },
      { name: 'Past Peak', badge: 'PAST', hex: '#795548', rgb: [121, 85, 72], rank: 5 },
      { name: 'Forest Senesced / Bare', badge: 'BARE', hex: '#78909c', rgb: [120, 144, 156], rank: 6 }
    ];

    let closest = palette[0];
    let minDist = Infinity;

    for (const p of palette) {
      const dr = r - p.rgb[0];
      const dg = g - p.rgb[1];
      const db = b - p.rgb[2];
      const dist = dr * dr + dg * dg + db * db;
      if (dist < minDist) {
        minDist = dist;
        closest = p;
      }
    }

    return {
      stageName: closest.name,
      badgeLabel: closest.badge,
      colorHex: closest.hex,
      rank: closest.rank
    };
  }

  /**
   * Samples the exact RGBA raster map pixel from a daily PMTiles layer for a given coordinate.
   */
  public static async sampleFoliagePixelForDate(
    manifest: Manifest,
    dateStr: string,
    lat: number,
    lon: number,
    zoom: number = 5,
    productId: any = 'foliage'
  ): Promise<{ stageName: string; badgeLabel: string; colorHex: string; rank: number } | null> {
    try {
      const tileUrl = getTileUrl(manifest, productId, 'seasonal', dateStr, '');
      if (!tileUrl) return null;

      const pmt = this.getPmtilesInstance(tileUrl);
      const n = 1 << zoom;
      const xExact = ((lon + 180.0) / 360.0) * n;
      const latRad = (lat * Math.PI) / 180.0;
      const yExact = (1.0 - Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad)) / Math.PI) / 2.0 * n;
      const tileX = Math.floor(xExact);
      const tileY = Math.floor(yExact);
      const px = Math.floor((xExact - tileX) * 256);
      const py = Math.floor((yExact - tileY) * 256);

      const tileResp = await pmt.getZxy(zoom, tileX, tileY);
      if (!tileResp || !tileResp.data) return null;

      const blob = new Blob([tileResp.data], { type: 'image/webp' });
      let imgBitmap: ImageBitmap | HTMLImageElement;
      if (typeof createImageBitmap === 'function') {
        imgBitmap = await createImageBitmap(blob);
      } else {
        imgBitmap = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
        });
      }

      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(imgBitmap, 0, 0);
      const pixel = ctx.getImageData(Math.max(0, Math.min(255, px)), Math.max(0, Math.min(255, py)), 1, 1).data;
      const [r, g, b, a] = pixel;

      return this.decodeFoliagePixel(r, g, b, a);
    } catch {
      return null;
    }
  }

  /**
   * Samples all days in the active month directly from the map PMTiles in parallel,
   * enforcing strict forward monotonic progression across the calendar so days never regress.
   */
  public static async sampleMonthTilePixels(
    manifest: Manifest,
    monthIndex: number,
    lat: number,
    lon: number,
    year: number = 2026,
    onDayPixelSampled?: (dateStr: string, sampled: { stageName: string; badgeLabel: string; colorHex: string; rank?: number }) => void,
    productId: any = 'foliage'
  ): Promise<void> {
    if (!manifest) return;
    const targetMonthNum = monthIndex + 9;
    const numDaysInMonth = new Date(year, targetMonthNum, 0).getDate();

    const sampleResults: Array<{
      day: number;
      dateStr: string;
      sampled: { stageName: string; badgeLabel: string; colorHex: string; rank: number } | null;
    }> = [];

    const tasks: Promise<void>[] = [];
    for (let d = 1; d <= numDaysInMonth; d++) {
      const mm = String(targetMonthNum).padStart(2, '0');
      const dd = String(d).padStart(2, '0');
      const dateStr = `${year}-${mm}-${dd}`;

      tasks.push(
        (async () => {
          const sampled = await this.sampleFoliagePixelForDate(manifest, dateStr, lat, lon, 5, productId);
          sampleResults.push({ day: d, dateStr, sampled });
        })()
      );
    }

    await Promise.allSettled(tasks);

    if (!onDayPixelSampled) return;

    const palette = [
      { name: 'Minimal / Patchy', badge: 'EARLY', hex: '#2e7d32', rank: 1 },
      { name: 'Moderate Color', badge: 'MOD', hex: '#fbc02d', rank: 2 },
      { name: 'Near Peak', badge: 'NEAR', hex: '#f57c00', rank: 3 },
      { name: 'Peak Foliage', badge: 'PEAK', hex: '#d32f2f', rank: 4 },
      { name: 'Past Peak', badge: 'PAST', hex: '#795548', rank: 5 },
      { name: 'Forest Senesced / Bare', badge: 'BARE', hex: '#78909c', rank: 6 }
    ];

    // Sort chronologically by day
    sampleResults.sort((a, b) => a.day - b.day);

    let maxSampledRank = 1;
    for (const res of sampleResults) {
      if (res.sampled) {
        if (res.sampled.rank < maxSampledRank) {
          const clamped = palette.find(p => p.rank === maxSampledRank);
          if (clamped) {
            res.sampled = {
              stageName: clamped.name,
              badgeLabel: clamped.badge,
              colorHex: clamped.hex,
              rank: clamped.rank
            };
          }
        } else {
          maxSampledRank = res.sampled.rank;
        }
        onDayPixelSampled(res.dateStr, res.sampled);
      }
    }
  }

  /**
   * Decodes category and badge from a Vibrancy map tile pixel [R, G, B, A].
   */
  public static decodeVibrancyPixel(r: number, g: number, b: number, a: number): {
    category: 'Below Normal / Reduced' | 'Average / Typical' | 'Above Normal / Enhanced';
    label: string;
    colorHex: string;
  } | null {
    if (a < 30) return null;

    // 1. Above Normal / Enhanced Vibrancy (Ruby Crimson & Electric Scarlet)
    // From color_schemes.py: r in [183..229], g in [28..57], b in [28..53]
    if (r >= 175 && g <= 70 && b <= 70) {
      return {
        category: 'Above Normal / Enhanced',
        label: 'Enhanced / Above Normal',
        colorHex: '#ff8a80'
      };
    }

    // 2. Below Normal / Reduced Vibrancy (Muted Russet & Khaki Brown)
    // From color_schemes.py: r in [141..188], g in [110..170], b in [99..164]
    if (b >= 75) {
      return {
        category: 'Below Normal / Reduced',
        label: 'Reduced / Below Normal',
        colorHex: '#bcaaa4'
      };
    }

    // 3. Average / Typical Vibrancy (Autumn Gold & Amber Pumpkin)
    // From color_schemes.py: r in [251..255], g in [140..179], b == 0
    return {
      category: 'Average / Typical',
      label: 'Typical / Average',
      colorHex: '#ffd54f'
    };
  }

  /**
   * Samples the Seasonal Vibrancy pixel from the foliage_vibrancy PMTiles layer.
   */
  public static async sampleVibrancyPixel(
    manifest: Manifest,
    lat: number,
    lon: number,
    treedOnly: boolean = false,
    zoom: number = 5
  ): Promise<{ category: 'Below Normal / Reduced' | 'Average / Typical' | 'Above Normal / Enhanced'; label: string; colorHex: string } | null> {
    try {
      const targetProduct = treedOnly ? 'foliage_vibrancy_treed' : 'foliage_vibrancy';
      const tileUrl = getTileUrl(manifest, targetProduct, 'seasonal', '', '');
      if (!tileUrl) return null;

      const pmt = this.getPmtilesInstance(tileUrl);
      const n = 1 << zoom;
      const xExact = ((lon + 180.0) / 360.0) * n;
      const latRad = (lat * Math.PI) / 180.0;
      const yExact = (1.0 - Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad)) / Math.PI) / 2.0 * n;
      const tileX = Math.floor(xExact);
      const tileY = Math.floor(yExact);
      const px = Math.floor((xExact - tileX) * 256);
      const py = Math.floor((yExact - tileY) * 256);

      const tileResp = await pmt.getZxy(zoom, tileX, tileY);
      if (!tileResp || !tileResp.data) return null;

      const blob = new Blob([tileResp.data], { type: 'image/webp' });
      let imgBitmap: ImageBitmap | HTMLImageElement;
      if (typeof createImageBitmap === 'function') {
        imgBitmap = await createImageBitmap(blob);
      } else {
        imgBitmap = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
        });
      }

      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(imgBitmap, 0, 0);
      const pixel = ctx.getImageData(Math.max(0, Math.min(255, px)), Math.max(0, Math.min(255, py)), 1, 1).data;
      const [r, g, b, a] = pixel;

      const decoded = this.decodeVibrancyPixel(r, g, b, a);
      if (!decoded && treedOnly) {
        // Fallback: if outside tree canopy in treed mode, sample full landscape layer
        return this.sampleVibrancyPixel(manifest, lat, lon, false, zoom);
      }
      return decoded;
    } catch {
      return null;
    }
  }

  /**
   * Decodes an RGBA pixel from foliage_peak_date into the calendar date and DOY.
   */
  public static decodePeakDatePixel(r: number, g: number, b: number, a: number): {
    doy: number;
    dateLabel: string;
    colorHex: string;
  } | null {
    if (a < 30) return null; // Outside deciduous forest canopy

    // Match to closest color step in the 62-day gradient (Sept 15 to Nov 15)
    let bestDoy = 258;
    let minDiff = Infinity;
    let bestR = 46, bestG = 125, bestB = 50;

    for (let doy = 258; doy <= 319; doy++) {
      const t = (doy - 258) / 61.0;
      let expR = 0, expG = 0, expB = 0;
      if (t <= (1.0 / 6.0)) {
        // 1. Green [46, 125, 50] -> Yellow [139, 195, 74]
        const s = t * 6.0;
        expR = 46 + (139 - 46) * s;
        expG = 125 + (195 - 125) * s;
        expB = 50 + (74 - 50) * s;
      } else if (t <= (2.0 / 6.0)) {
        // 2. Yellow [139, 195, 74] -> Gold [251, 192, 45]
        const s = (t - (1.0 / 6.0)) * 6.0;
        expR = 139 + (251 - 139) * s;
        expG = 195 + (192 - 195) * s;
        expB = 74 + (45 - 74) * s;
      } else if (t <= (3.0 / 6.0)) {
        // 3. Gold [251, 192, 45] -> Orange [245, 124, 0]
        const s = (t - (2.0 / 6.0)) * 6.0;
        expR = 251 + (245 - 251) * s;
        expG = 192 + (124 - 192) * s;
        expB = 45 + (0 - 45) * s;
      } else if (t <= (4.0 / 6.0)) {
        // 4. Orange [245, 124, 0] -> Red [211, 47, 47]
        const s = (t - (3.0 / 6.0)) * 6.0;
        expR = 245 + (211 - 245) * s;
        expG = 124 + (47 - 124) * s;
        expB = 0 + (47 - 0) * s;
      } else if (t <= (5.0 / 6.0)) {
        // 5. Red [211, 47, 47] -> Brown [121, 85, 72]
        const s = (t - (4.0 / 6.0)) * 6.0;
        expR = 211 + (121 - 211) * s;
        expG = 47 + (85 - 47) * s;
        expB = 47 + (72 - 47) * s;
      } else {
        // 6. Brown [121, 85, 72] -> Grey [120, 144, 156]
        const s = (t - (5.0 / 6.0)) * 6.0;
        expR = 121 + (120 - 121) * s;
        expG = 85 + (144 - 85) * s;
        expB = 72 + (156 - 72) * s;
      }

      const diff = Math.hypot(r - expR, g - expG, b - expB);
      if (diff < minDiff) {
        minDiff = diff;
        bestDoy = doy;
        bestR = Math.round(expR);
        bestG = Math.round(expG);
        bestB = Math.round(expB);
      }
    }

    // Convert DOY in a non-leap year (e.g. 2026) to calendar month and day
    const year = 2026;
    const dateObj = new Date(Date.UTC(year, 0, 1));
    dateObj.setUTCDate(bestDoy);
    const monthShort = dateObj.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
    const dayOfMonth = dateObj.getUTCDate();

    let dateLabel = `${monthShort} ${dayOfMonth}`;
    if (bestDoy <= 258) {
      dateLabel = 'Sept 15 or earlier';
    } else if (bestDoy >= 319) {
      dateLabel = 'Nov 15 or later';
    }

    const hex = `#${bestR.toString(16).padStart(2, '0')}${bestG.toString(16).padStart(2, '0')}${bestB.toString(16).padStart(2, '0')}`;

    return {
      doy: bestDoy,
      dateLabel,
      colorHex: hex
    };
  }

  /**
   * Samples the Peak Color by Date pixel from the foliage_peak_date PMTiles layer.
   */
  public static async samplePeakDatePixel(
    manifest: Manifest,
    lat: number,
    lon: number,
    treedOnly: boolean = false,
    zoom: number = 5
  ): Promise<{ doy: number; dateLabel: string; colorHex: string } | null> {
    try {
      const targetProduct = treedOnly ? 'foliage_peak_date_treed' : 'foliage_peak_date';
      const tileUrl = getTileUrl(manifest, targetProduct, 'seasonal', '', '');
      if (!tileUrl) return null;

      const pmt = this.getPmtilesInstance(tileUrl);
      const n = 1 << zoom;
      const xExact = ((lon + 180.0) / 360.0) * n;
      const latRad = (lat * Math.PI) / 180.0;
      const yExact = (1.0 - Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad)) / Math.PI) / 2.0 * n;
      const tileX = Math.floor(xExact);
      const tileY = Math.floor(yExact);
      const px = Math.floor((xExact - tileX) * 256);
      const py = Math.floor((yExact - tileY) * 256);

      const tileResp = await pmt.getZxy(zoom, tileX, tileY);
      if (!tileResp || !tileResp.data) return null;

      const blob = new Blob([tileResp.data], { type: 'image/webp' });
      let imgBitmap: ImageBitmap | HTMLImageElement;
      if (typeof createImageBitmap === 'function') {
        imgBitmap = await createImageBitmap(blob);
      } else {
        imgBitmap = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
        });
      }

      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(imgBitmap, 0, 0);
      const pixel = ctx.getImageData(Math.max(0, Math.min(255, px)), Math.max(0, Math.min(255, py)), 1, 1).data;
      const [r, g, b, a] = pixel;

      const decoded = this.decodePeakDatePixel(r, g, b, a);
      if (!decoded && treedOnly) {
        // Fallback: if outside tree canopy in treed mode, sample full landscape layer
        return this.samplePeakDatePixel(manifest, lat, lon, false, zoom);
      }
      return decoded;
    } catch {
      return null;
    }
  }
}

export default FoliageService;
